import type { ImportCheck, ImportOutcome, ImportPreview } from '@fundline/shared/schemas/imports';
import {
  importAttestation,
  IMPORT_OUTCOME_LABELS,
  PERSON_CONSENT_SOURCE_LABELS,
  PERSON_CONSENT_SOURCES,
} from '@fundline/shared/schemas/imports';
import { formatCentsAmount } from '@fundline/shared/money';
import { formatPhone } from '@fundline/shared/phone';
import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Badge } from '../../components/ui/Badge.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { SelectField, TextField } from '../../components/ui/Field.tsx';
import { Card, PageHeader, Spinner } from '../../components/ui/Layout.tsx';
import { useWorkspaceClaims } from '../../lib/auth.tsx';
import { localIsoDate } from '../compliance/consent-date.ts';
import { useIntakeSourcesQuery } from '../intake/use-intake.ts';
import { useMembersQuery } from '../members/use-members.ts';
import { useFieldDefinitionsQuery } from '../settings/use-fields.ts';
import { useTagsQuery } from '../settings/use-tags.ts';
import { useWorkspaceQuery } from '../settings/use-workspace.ts';
import {
  batchTagLabel,
  checkKey,
  consentProblems,
  countLines,
  type ImportFormState,
  lookupCostText,
  mappingProblem,
  settingsRequest,
  startRequest,
  targetOptions,
} from './import-form.ts';
import { IMPORT_STATUS } from './ImportsPage.tsx';
import {
  downloadImportFile,
  downloadImportReport,
  type ImportSummary,
  useArchiveImported,
  useCheckImport,
  useImportIssuesQuery,
  useImportPreviewQuery,
  useImportQuery,
  useStartImport,
} from './use-imports.ts';

/** What happened to one row, in words (a new merchant is "Imported", not "New merchants"). */
function rowResult(outcome: ImportOutcome): string {
  if (outcome === 'created') return 'Imported';
  if (outcome === 'updated') return 'Filled in';
  return IMPORT_OUTCOME_LABELS[outcome];
}

/** "1 new merchant", "12 new merchants". */
function merchantsText(n: number): string {
  return `${n.toLocaleString('en-US')} new ${n === 1 ? 'merchant' : 'merchants'}`;
}

const LINK_BUTTON =
  'fl-btn fl-btn--ghost focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring';

/** One import: set it up (columns, people, consent) until it starts, then its progress and results. */
export function ImportDetailPage({ importId }: { importId: string }) {
  const { workspaceId } = useWorkspaceClaims();
  const imp = useImportQuery(workspaceId, importId);
  const notStarted = imp.data?.status === 'uploaded' || imp.data?.status === 'mapped';
  const preview = useImportPreviewQuery(workspaceId, importId, notStarted);
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={imp.data ? imp.data.fileName : 'Import'}
        description={
          imp.data
            ? `${imp.data.totalRows.toLocaleString('en-US')} rows · uploaded ${new Date(imp.data.createdAt).toLocaleString()}`
            : undefined
        }
        actions={
          <Link to="/merchants/imports" className={LINK_BUTTON}>
            All imports
          </Link>
        }
      />
      {imp.isPending ? <Spinner label="Loading the import" /> : null}
      {imp.isError ? <Alert tone="error">{imp.error.message}</Alert> : null}
      {imp.data && notStarted ? (
        preview.isPending ? (
          <Spinner label="Reading the file" />
        ) : preview.isError ? (
          <Alert tone="error">{preview.error.message}</Alert>
        ) : preview.data ? (
          <ImportSetup preview={preview.data} />
        ) : null
      ) : null}
      {imp.data && !notStarted ? <ImportResult imp={imp.data} /> : null}
    </div>
  );
}

function ImportSetup({ preview }: { preview: ImportPreview }) {
  const { workspaceId, userId } = useWorkspaceClaims();
  const fields = useFieldDefinitionsQuery(workspaceId);
  const members = useMembersQuery(workspaceId);
  const tags = useTagsQuery(workspaceId);
  const sources = useIntakeSourcesQuery(workspaceId);
  const workspace = useWorkspaceQuery(workspaceId);
  const check = useCheckImport(workspaceId, preview.importId);
  const start = useStartImport(workspaceId, preview.importId);
  const today = localIsoDate(new Date());

  const [form, setForm] = useState<ImportFormState>(() => ({
    mapping: Object.fromEntries(
      preview.columns.map((_, i) => [String(i), preview.suggestedMapping[String(i)] ?? '']),
    ),
    sourceId: preview.sourceId,
    saveMappingAs: '',
    assignmentMode: 'single',
    assignee: userId,
    roundRobin: [],
    tagIds: [],
    batchTag: true,
    existingMode: 'skip',
    lookup: false,
    consentSource: '',
    consentDetail: '',
    consentScope: '',
    consentOnOrBefore: today,
    attested: false,
  }));
  const [checkedKey, setCheckedKey] = useState<string | null>(null);
  const [shown, setShown] = useState(false);
  const set = <K extends keyof ImportFormState>(key: K, value: ImportFormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const options = useMemo(() => targetOptions(fields.data ?? []), [fields.data]);
  const active = (members.data ?? []).filter((m) => m.status === 'active');
  const vendorName = sources.data?.find((s) => s.id === form.sourceId)?.name ?? null;
  const problem = mappingProblem(form.mapping);
  const peopleProblem =
    form.assignmentMode === 'single'
      ? form.assignee
        ? null
        : 'Choose who gets these merchants'
      : form.roundRobin.length > 0
        ? null
        : 'Choose at least one person for round robin';
  const consent = consentProblems(form, today);
  const fresh = check.data && checkedKey === checkKey(form) ? check.data : null;

  function runCheck() {
    setShown(true);
    if (problem || peopleProblem) return;
    const key = checkKey(form);
    check.mutate(settingsRequest(form), { onSuccess: () => setCheckedKey(key) });
  }

  function runStart() {
    setShown(true);
    if (!fresh || Object.keys(consent).length > 0) return;
    const body = startRequest(form, fresh);
    if (body) start.mutate(body);
  }

  if (fields.isPending || members.isPending) return <Spinner />;
  return (
    <div className="flex flex-col gap-5">
      <Card title="Columns">
        <div className="flex flex-col gap-4">
          <p className="callout text-ink-muted">
            Read as {preview.encoding}
            {preview.delimiter === '\t'
              ? ', tab separated'
              : preview.delimiter === ';'
                ? ', semicolons'
                : ''}
            . If letters look wrong below (Ã© instead of é), save the file as &ldquo;CSV
            UTF-8&rdquo; and upload it again. The time zone is never imported: it comes from the
            state and area code.
          </p>
          <ul className="flex flex-col divide-y divide-hairline" aria-label="Columns">
            {preview.columns.map((column, i) => {
              const samples = preview.sample
                .map((r) => r[i] ?? '')
                .filter((v) => v.trim() !== '')
                .slice(0, 3);
              return (
                <li
                  key={`${column}-${i}`}
                  className="grid gap-2 py-3 sm:grid-cols-2 sm:items-center"
                  data-testid="mapping-row"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-ink">{column}</p>
                    <p className="truncate footnote text-ink-muted">
                      {samples.length ? samples.join(' · ') : 'Empty in the first rows'}
                    </p>
                  </div>
                  <SelectField
                    label={`Import "${column}" as`}
                    value={form.mapping[String(i)] ?? ''}
                    onChange={(e) =>
                      set('mapping', {
                        ...form.mapping,
                        [String(i)]: options.find((o) => o.value === e.target.value)?.value ?? '',
                      })
                    }
                    options={options}
                  />
                </li>
              );
            })}
          </ul>
          {shown && problem ? <Alert tone="error">{problem}</Alert> : null}
        </div>
      </Card>

      <Card title="Who gets them">
        <div className="flex flex-col gap-4">
          <fieldset className="flex flex-col gap-2">
            <legend className="callout font-semibold text-ink">Assign new merchants to</legend>
            {(['single', 'round_robin'] as const).map((mode) => (
              <label key={mode} className="flex items-center gap-2 callout text-ink">
                <input
                  type="radio"
                  name="assignment"
                  className="size-4"
                  checked={form.assignmentMode === mode}
                  onChange={() => set('assignmentMode', mode)}
                />
                {mode === 'single' ? 'One person' : 'Round robin, in file order'}
              </label>
            ))}
          </fieldset>
          {form.assignmentMode === 'single' ? (
            <SelectField
              label="Person"
              value={form.assignee}
              onChange={(e) => set('assignee', e.target.value)}
              options={active.map((m) => ({
                value: m.userId,
                label: `${m.fullName || m.email} (${m.role})`,
              }))}
            />
          ) : (
            <fieldset className="flex flex-col gap-2">
              <legend className="callout font-semibold text-ink">People</legend>
              {active.map((m) => (
                <label key={m.userId} className="flex items-center gap-2 callout text-ink">
                  <input
                    type="checkbox"
                    className="size-4"
                    checked={form.roundRobin.includes(m.userId)}
                    onChange={(e) =>
                      set(
                        'roundRobin',
                        e.target.checked
                          ? [...form.roundRobin, m.userId]
                          : form.roundRobin.filter((id) => id !== m.userId),
                      )
                    }
                  />
                  {m.fullName || m.email} ({m.role})
                </label>
              ))}
            </fieldset>
          )}
          {shown && peopleProblem ? <Alert tone="error">{peopleProblem}</Alert> : null}

          <fieldset className="flex flex-col gap-2">
            <legend className="callout font-semibold text-ink">
              Numbers already in your workspace
            </legend>
            <label className="flex items-center gap-2 callout text-ink">
              <input
                type="radio"
                name="existing"
                className="size-4"
                checked={form.existingMode === 'skip'}
                onChange={() => set('existingMode', 'skip')}
              />
              Skip them
            </label>
            <label className="flex items-start gap-2 callout text-ink">
              <input
                type="radio"
                name="existing"
                className="mt-0.5 size-4"
                checked={form.existingMode === 'fill_blanks'}
                onChange={() => set('existingMode', 'fill_blanks')}
              />
              Fill in blank fields only (never overwrites, never reassigns)
            </label>
          </fieldset>

          {tags.data && tags.data.length > 0 ? (
            <fieldset className="flex flex-col gap-2">
              <legend className="callout font-semibold text-ink">Tags</legend>
              {tags.data.map((t) => (
                <label key={t.id} className="flex items-center gap-2 callout text-ink">
                  <input
                    type="checkbox"
                    className="size-4"
                    checked={form.tagIds.includes(t.id)}
                    onChange={(e) =>
                      set(
                        'tagIds',
                        e.target.checked
                          ? [...form.tagIds, t.id]
                          : form.tagIds.filter((x) => x !== t.id),
                      )
                    }
                  />
                  {t.name}
                </label>
              ))}
            </fieldset>
          ) : null}
          <label className="flex items-start gap-2 callout text-ink">
            <input
              type="checkbox"
              className="mt-0.5 size-4"
              checked={form.batchTag}
              onChange={(e) => set('batchTag', e.target.checked)}
            />
            Also tag them &ldquo;{batchTagLabel(preview.fileName, new Date())}&rdquo;
          </label>
          {form.sourceId ? (
            <p className="callout text-ink-muted">
              The columns you pick are saved for {vendorName ?? 'this vendor'}.
            </p>
          ) : (
            <TextField
              label="Save these columns for a vendor (optional)"
              hint="Next time, choose this vendor and the columns are picked for you."
              placeholder="Vendor name"
              value={form.saveMappingAs}
              onChange={(e) => set('saveMappingAs', e.target.value)}
              maxLength={80}
            />
          )}
        </div>
      </Card>

      <Card title="Carrier lookup">
        <div className="flex flex-col gap-2">
          <label className="flex items-start gap-2 callout text-ink">
            <input
              type="checkbox"
              className="mt-0.5 size-4"
              checked={form.lookup}
              onChange={(e) => set('lookup', e.target.checked)}
              data-testid="lookup-toggle"
            />
            Look up new numbers and skip landlines (paid, about $0.004 per new number)
          </label>
          {fresh ? (
            <p className="callout text-ink-muted" data-testid="lookup-cost">
              {lookupCostText(fresh.lookupCostCents, fresh.lookupsNeeded)}
            </p>
          ) : null}
        </div>
      </Card>

      <Card title="Consent">
        <div className="flex flex-col gap-4">
          <p className="callout text-ink-muted">
            Recorded with every merchant this file adds. A consent date column, if you mapped one,
            is used per row and rows without a date are skipped.
          </p>
          <SelectField
            label="Where did consent come from?"
            value={form.consentSource}
            onChange={(e) =>
              set('consentSource', PERSON_CONSENT_SOURCES.find((s) => s === e.target.value) ?? '')
            }
            options={[
              { value: '', label: 'Choose one' },
              ...PERSON_CONSENT_SOURCES.map((s) => ({
                value: s,
                label: PERSON_CONSENT_SOURCE_LABELS[s],
              })),
            ]}
            error={shown ? consent.consentSource : null}
            data-testid="import-consent-source"
          />
          <TextField
            label="Vendor or form (optional)"
            placeholder="e.g. Acme Leads"
            value={form.consentDetail}
            onChange={(e) => set('consentDetail', e.target.value)}
            maxLength={300}
          />
          <TextField
            label="What they agreed to"
            placeholder="Texts about business funding offers"
            value={form.consentScope}
            onChange={(e) => set('consentScope', e.target.value)}
            error={shown ? consent.consentScope : null}
            maxLength={500}
          />
          <TextField
            label="Consent was given on or before"
            type="date"
            max={today}
            value={form.consentOnOrBefore}
            onChange={(e) => set('consentOnOrBefore', e.target.value)}
            error={shown ? consent.consentOnOrBefore : null}
          />
          <label className="flex items-start gap-2 callout text-ink">
            <input
              type="checkbox"
              className="mt-0.5 size-4"
              checked={form.attested}
              onChange={(e) => set('attested', e.target.checked)}
              data-testid="import-attest"
            />
            {importAttestation(workspace.data?.name ?? 'this workspace')}
          </label>
          {shown && consent.attested ? <Alert tone="error">{consent.attested}</Alert> : null}
        </div>
      </Card>

      {check.isError ? <Alert tone="error">{check.error.message}</Alert> : null}
      {fresh ? <CheckSummary check={fresh} /> : null}
      {start.isError ? <Alert tone="error">{start.error.message}</Alert> : null}
      <div className="flex flex-wrap gap-2">
        <Button variant={fresh ? 'secondary' : 'primary'} busy={check.isPending} onClick={runCheck}>
          Check file
        </Button>
        {fresh ? (
          <Button
            busy={start.isPending}
            onClick={runStart}
            disabled={fresh.counts.created + fresh.counts.updated === 0}
          >
            Import {fresh.counts.created.toLocaleString('en-US')} new{' '}
            {fresh.counts.created === 1 ? 'merchant' : 'merchants'}
          </Button>
        ) : (
          <p className="callout self-center text-ink-muted">
            Check the file to see what will happen.
          </p>
        )}
      </div>
    </div>
  );
}

function CheckSummary({ check }: { check: ImportCheck }) {
  return (
    <Card title="What will happen">
      <div className="flex flex-col gap-3" data-testid="check-summary">
        <ul className="flex flex-col gap-1">
          {countLines(check.counts).map((l) => (
            <li
              key={l.outcome}
              className="flex justify-between gap-3 callout"
              data-testid={`count-${l.outcome}`}
            >
              <span className="text-ink">{l.label}</span>
              <span className="fl-num font-semibold text-ink">
                {l.count.toLocaleString('en-US')}
              </span>
            </li>
          ))}
        </ul>
        {check.needsState > 0 ? (
          <Alert tone="warning">
            {merchantsText(check.needsState)} with no state and a number without a time zone
            (toll-free) will be imported, but nobody can text them until someone adds their state.
          </Alert>
        ) : null}
        {check.warnings > 0 ? (
          <p className="callout text-ink-muted">
            {check.warnings === 1
              ? '1 row has a value that doesn’t fit its field. That value is left out; the row is still imported.'
              : `${check.warnings.toLocaleString('en-US')} rows have values that don’t fit their field. Those values are left out; the rows are still imported.`}
          </p>
        ) : null}
        {check.issues.length > 0 ? (
          <details>
            <summary className="callout cursor-pointer font-semibold text-ink">
              First rows with issues
            </summary>
            <ul className="mt-2 flex flex-col gap-1">
              {check.issues.map((i) => (
                <li key={`${i.row}-${i.outcome}`} className="footnote text-ink-muted">
                  Line {i.row}:{' '}
                  {i.reason ??
                    IMPORT_OUTCOME_LABELS[i.outcome as keyof typeof IMPORT_OUTCOME_LABELS]}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </div>
    </Card>
  );
}

function ImportResult({ imp }: { imp: ImportSummary }) {
  const { workspaceId } = useWorkspaceClaims();
  const finished = imp.status === 'completed' || imp.status === 'failed';
  const issues = useImportIssuesQuery(workspaceId, imp.id, finished);
  const archive = useArchiveImported(workspaceId, imp.id);
  const [confirming, setConfirming] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const percent = imp.totalRows ? Math.round((imp.processedRows / imp.totalRows) * 100) : 100;

  async function download(kind: 'report' | 'file') {
    setDownloadError(null);
    try {
      await (kind === 'report'
        ? downloadImportReport(workspaceId, imp.id)
        : downloadImportFile(workspaceId, imp.id));
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : "Couldn't download the file.");
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <div className="flex flex-col gap-3" data-testid="import-result">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Badge role={IMPORT_STATUS[imp.status].role} testId="import-status">
              {IMPORT_STATUS[imp.status].label}
            </Badge>
            <span className="callout text-ink-muted">
              {imp.processedRows.toLocaleString('en-US')} of {imp.totalRows.toLocaleString('en-US')}{' '}
              rows
            </span>
          </div>
          <div
            className="h-2 w-full overflow-hidden rounded-pill bg-surface"
            role="progressbar"
            aria-label="Import progress"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
          >
            <div className="h-full bg-brand" style={{ width: `${percent}%` }} />
          </div>
          {imp.error ? <Alert tone="error">{imp.error}</Alert> : null}
          <ul className="flex flex-col gap-1">
            {countLines(imp.counts).map((l) => (
              <li
                key={l.outcome}
                className="flex justify-between gap-3 callout"
                data-testid={`result-${l.outcome}`}
              >
                <span className="text-ink">{l.label}</span>
                <span className="fl-num font-semibold text-ink">
                  {l.count.toLocaleString('en-US')}
                </span>
              </li>
            ))}
          </ul>
          {imp.needsState > 0 ? (
            <Alert tone="warning">
              {merchantsText(imp.needsState)} {imp.needsState === 1 ? 'needs' : 'need'} a state
              before anyone can text {imp.needsState === 1 ? 'it' : 'them'}. The report lists{' '}
              {imp.needsState === 1 ? 'it' : 'them'}.
            </Alert>
          ) : null}
          {imp.lookupCount > 0 ? (
            <p className="callout text-ink-muted">
              Carrier lookups: {imp.lookupCount.toLocaleString('en-US')} (
              {formatCentsAmount(imp.lookupCostCents)})
            </p>
          ) : null}
          <p className="footnote text-ink-muted">
            Consent recorded:{' '}
            {PERSON_CONSENT_SOURCE_LABELS[
              imp.consentSource as keyof typeof PERSON_CONSENT_SOURCE_LABELS
            ] ?? '—'}
            {imp.consentSourceDetail ? ` · ${imp.consentSourceDetail}` : ''}
            {imp.consentOnOrBefore ? ` · on or before ${imp.consentOnOrBefore}` : ''}
          </p>
        </div>
      </Card>

      {finished ? (
        <Card title="Rows with issues">
          <div className="flex flex-col gap-3">
            {issues.isPending ? <Spinner label="Loading rows" /> : null}
            {issues.isError ? <Alert tone="error">{issues.error.message}</Alert> : null}
            {issues.data?.length === 0 ? <p className="callout text-ink-muted">None.</p> : null}
            {issues.data && issues.data.length > 0 ? (
              <ul className="flex flex-col divide-y divide-hairline" data-testid="import-issues">
                {issues.data.map((i) => (
                  <li key={i.row} className="flex flex-col py-2 callout">
                    <span className="text-ink">
                      Line {i.row}
                      {i.phone ? ` · ${formatPhone(i.phone)}` : ''} · {rowResult(i.outcome)}
                    </span>
                    {i.reason ? (
                      <span className="break-words footnote text-ink-muted">{i.reason}</span>
                    ) : null}
                    {i.warnings.map((w) => (
                      <span key={w} className="break-words footnote text-ink-muted">
                        {w}
                      </span>
                    ))}
                  </li>
                ))}
              </ul>
            ) : null}
            <p className="footnote text-ink-muted">
              The report lists every row; this shows the first 100.
            </p>
            {downloadError ? <Alert tone="error">{downloadError}</Alert> : null}
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => void download('report')}>
                Download report
              </Button>
              <Button variant="ghost" onClick={() => void download('file')}>
                Download original file
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      {finished && imp.counts.created > 0 ? (
        <Card title="Undo">
          <div className="flex flex-col gap-3">
            <p className="callout text-ink-muted">
              Archives the merchants this import added that nobody has texted yet. Nothing is
              deleted.
              {imp.archivedCount > 0
                ? ` ${imp.archivedCount.toLocaleString('en-US')} archived so far.`
                : ''}
            </p>
            {archive.isError ? <Alert tone="error">{archive.error.message}</Alert> : null}
            {archive.data ? (
              <Alert tone="success">
                Archived {archive.data.archived.toLocaleString('en-US')} merchants.
              </Alert>
            ) : null}
            {confirming ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="danger"
                  busy={archive.isPending}
                  onClick={() =>
                    archive.mutate(undefined, { onSettled: () => setConfirming(false) })
                  }
                >
                  Yes, archive them
                </Button>
                <Button variant="ghost" onClick={() => setConfirming(false)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <div>
                <Button variant="secondary" onClick={() => setConfirming(true)}>
                  Archive this import&rsquo;s new merchants
                </Button>
              </div>
            )}
          </div>
        </Card>
      ) : null}
    </div>
  );
}
